import React from 'react';
import { useQuery } from 'react-query';
import { Allergy, MedicalCondition, Medication, MedicationAllergy, PetInterface } from 'types/pet';
import classNames from 'classnames';
import './overview.scss';
import { SimplePet } from '@chewy/pet-profile-micro-interaction/dist/esm/packages/micro-interaction/src/services/types/petApi';
import useLocationQuery from '../../hooks/useLocationQuery';
import { MicroInteractionWrapper } from '../../components/microinteraction/MicroInteractionWrapper';
import EditActionChip from '../../components/edit-profile-button/EditProfileButton';
import { featureMintOnViewLoadData } from '../../api/featuresApi';
import { MINT_ON_VIEW_ENABLED } from '../../util/config';
import getImageUrl from '../../util/getImageUrl';
import PetProfileAvatar from './pet-profile-avatar/PetProfileAvatar';

type OverviewProps = {
  petProfile: PetInterface;
  setPetProfile: React.Dispatch<React.SetStateAction<PetInterface>>;
};

const Overview = ({ petProfile, setPetProfile }: OverviewProps) => {
  const query = useLocationQuery();

  const showMintQuery = query.get('showMint') === 'true';
  const noShowMintQuery = query.get('showMint') === 'false';

  const { data: featureFlagData, isLoading: isFeatureFlagLoading } = useQuery(
    featureMintOnViewLoadData.key,
    featureMintOnViewLoadData,
  );

  const showMint =
    !noShowMintQuery &&
    (showMintQuery || featureFlagData?.[MINT_ON_VIEW_ENABLED]?.toLowerCase() === 'true');

  // this should be a temp solution, ideally the mint package would provide type that covers all data returned
  // duplicates logic in transformPetPetProfileA
  const refreshProfileData = (oldData: PetInterface, newData: SimplePet): PetInterface => ({
    ...oldData,
    avatarId: newData.avatar?.id || 0,
    avatarUrl:
      newData.avatar?.imageSource === 'PHOTO'
        ? getImageUrl(newData.avatar?.imageUri, 125, 'SS')
        : newData.avatar?.imageUri || oldData.avatarUrl,
    avatarSource: newData.avatar?.imageSource,
    weightLbs: newData.weight || undefined,
    allergies: (newData.allergies as Allergy[]) || [],
    medications: (newData.medications as Medication[]) || [],
    medicationAllergies: (newData.medicationAllergies as MedicationAllergy[]) || [],
    medicalConditions: (newData.medicalConditions as MedicalCondition[]) || [],
  });

  return (
    <div className="pet-profile__overview">
      <section>
        <div className="pet-profile__header">
          <PetProfileAvatar
            imageUrl={petProfile.avatarUrl}
            className={classNames('pet-profile__avatar')}
            data-testid="pet-details-avatar"
          />
        </div>
        <div className="pet-profile__header-layout">
          <header className="pet-profile__overview__header">
            <h1
              className="pet-profile__overview__header__text"
              data-testid="pet-details-pet-name-value"
            >
              {petProfile.name}
            </h1>
          </header>
          <div className="pet-profile__overview__edit">
            <EditActionChip
              petProfileId={petProfile.id}
              name={petProfile.name}
              className="pet-profile__edit-button--btn"
            />
          </div>
        </div>
        {!isFeatureFlagLoading && showMint && (
          <MicroInteractionWrapper
            petId={petProfile.id}
            onUpdateSuccess={(newData: SimplePet) => {
              setPetProfile((oldData: PetInterface) => refreshProfileData(oldData, newData));
            }}
          />
        )}
      </section>
    </div>
  );
};

export default Overview;
